import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import React from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { MobiliAvatar } from "../../atoms/MobiliAvatar";
import { getBsnrColor, resolveTicketBsnr, resolveTicketClinicId } from "../../helpers/bsnrColor";
import { mobiliTheme } from "../../themes/mobiliTheme";
import { TicketDto } from "../../api-client";

const RxOrdersTable: React.FC<{
  handleRowClick: (ticket: any) => void;
  ticketsData: TicketDto[];
  hasMoreData: boolean;
  fetchMoreData: () => void;
  isLoading: boolean;
  scrollId?: string;
  groupByClinic?: boolean;
}> = ({
  handleRowClick,
  ticketsData,
  hasMoreData,
  fetchMoreData,
  isLoading,
  scrollId = "scrollableRxOrdersDiv",
  groupByClinic = false,
}) => {

  // Auto-prefetch while container not scrollable
  React.useEffect(() => {
    if (!hasMoreData || isLoading) return;
    const el = document.getElementById(scrollId);
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight) {
      // Defer to next frame to avoid synchronous re-render loops
      const id = requestAnimationFrame(() => fetchMoreData());
      return () => cancelAnimationFrame(id);
    }
  }, [ticketsData.length, hasMoreData, isLoading, fetchMoreData, scrollId]);

  return (
    <TableContainer
      id={scrollId}
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
      }}
    >
      <InfiniteScroll
        dataLength={ticketsData.length}
        hasMore={hasMoreData}
        next={() => {
          if (!isLoading && hasMoreData) fetchMoreData();
        }}
        loader={
          isLoading && hasMoreData ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
              <CircularProgress size={22} />
            </Box>
          ) : null
        }
        endMessage={
          !hasMoreData && ticketsData.length > 0 ? (
            <Box sx={{ textAlign: "center", py: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Alle Rezept Bestellungen geladen
              </Typography>
            </Box>
          ) : null
        }
        scrollableTarget={scrollId}
        style={{ overflow: "visible" }}
        scrollThreshold={0.85}
      >
        <Table size="small" stickyHeader>
          <TableHead
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              background: mobiliTheme.cardBackground,
            }}
          >
            <TableRow>
              <TableCell align="center">ID#</TableCell>
              <TableCell align="center">Praxis (BSNR)</TableCell>
              <TableCell align="center">Datum</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Erstellt</TableCell>
              <TableCell align="center">Betroffen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ticketsData.map((t, i) => {
              const bsnr = resolveTicketBsnr(t);
              const clinicId = resolveTicketClinicId(t);
              const prevClinicId = i > 0 ? resolveTicketClinicId(ticketsData[i - 1]) : null;
              // Only draw the group divider when the list is actually sorted/grouped by clinic.
              const isNewClinicGroup = groupByClinic && i > 0 && clinicId !== prevClinicId;
              return (
                <TableRow
                  hover
                  key={`rx-order-ticket-${t.id}-${i}`}
                  onClick={() => handleRowClick(t)}
                  sx={{
                    cursor: "pointer",
                    textAlign: "center",
                    ...(isNewClinicGroup && {
                      "& > td": {
                        borderTop: `1px solid ${mobiliTheme.palette.secondary.main}`,
                      },
                    }),
                  }}
                >
                  <TableCell>{t.id}</TableCell>
                  <TableCell>{bsnr ?? "-"}</TableCell>
                  <TableCell>
                    {t.createdAt ? format(new Date(t.createdAt), "dd.MM.yyyy") : "-"}
                  </TableCell>
                  <TableCell>{t.ticketStatus}</TableCell>
                  <TableCell>
                    <MobiliAvatar user={t.createdByUser} sx={{ width: 26, height: 26 }} />
                  </TableCell>
                  <TableCell>
                    {t && t.patient ? `${t.patient.firstName} ${t.patient.lastName}` : "-"}
                  </TableCell>
                </TableRow>
              );
            })}

            {ticketsData.length === 0 && isLoading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </InfiniteScroll>
    </TableContainer>
  );
};

export default RxOrdersTable;
